import { getCSRFToken } from '/app/app/assets/js/utils/security.js'
import { flashInfo, flashFailure } from '/app/app/assets/js/utils/flash.js'
import { renderStreamMessage } from '@hotwired/turbo'

export default (path, presignPath, errorPath, jobId) => ({
  items: [],
  touched: false,
  submitting: false,
  maxSize: null,
  maxSizeError: null,

  init() {
    if (this.$refs.input) {
      this.$refs.input.value = ''
      this.maxSize = Number(this.$refs.input.dataset?.maxSize) || null
      this.maxSizeError = this.$refs.input.dataset?.maxSizeError || null
    }
    this.items = storeFor(path, jobId).items
  },

  busy() {
    return this.items.some((item) => item.status === 'uploading')
  },

  start() {
    if (!this.validate()) return

    const store = storeFor(path, jobId)

    for (let i = store.items.length - 1; i >= 0; i--)
      if (store.items[i].status !== 'uploading') store.items.splice(i, 1)

    for (const file of this.$refs.input.files) {
      const raw = {
        key: `${file.name}:${file.size}:${Math.random().toString(36).slice(2)}`,
        name: file.name,
        progress: 0,
        status: 'uploading',
        error: null,
      }
      // Read back through the reactive array so mutations stay reactive
      // across component re-renders.
      store.items.push(raw)
      const item = store.items[store.items.length - 1]
      store.queue.push({ file, item })
    }
    this.$refs.input.value = ''

    this.pump(store)
  },

  validate() {
    if (this.$refs.input.files.length == 0) {
      flashFailure('Please select at least one file')
      return false
    }

    if (this.maxSize && this.maxSizeError) {
      const oversized = [...this.$refs.input.files].some(
        (file) => file.size > this.maxSize,
      )
      if (oversized) {
        flashFailure(this.maxSizeError)
        return false
      }
    }

    return true
  },

  // A user-initiated submission (purpose pick, edit, delete) is in flight.
  // While it is, every control in the card is disabled, so a stream response
  // can never land under an open dropdown or a half-finished interaction.
  // turbo:submit-end (bound on the card element) resets it, success or
  // failure alike.
  confirmDelete(button) {
    if (!window.confirm(button.dataset.deleteConfirm)) return

    this.submitting = true
    button.closest('form').requestSubmit()
  },

  cancel() {
    const store = storeFor(path, jobId)
    for (const xhr of store.xhrs) xhr.abort()

    store.queue.length = 0
    store.items.length = 0

    this.init()
    flashInfo('Job upload canceled')
  },

  dismiss(item) {
    removeRow(storeFor(path, jobId), item)
  },

  pump(store) {
    while (store.active < CONCURRENCY && store.queue.length > 0)
      this.run(store, store.queue.shift())
  },

  async run(store, entry) {
    store.active++
    const { file, item } = entry
    const retryable = () => store.items.includes(item)

    try {
      const presigned = await retry(() => this.presign(file), retryable)
      const type =
        presigned.headers?.['Content-Type'] ||
        file.type ||
        'application/octet-stream'
      await retry(
        () => this.put(presigned.url, file, type, item, store),
        retryable,
      )
      await retry(
        () => this.attach(file, presigned.location, item, store),
        retryable,
        RETRY_ATTEMPTS,
        (error) => isNetworkFailure(error) || isGatewayFailure(error),
      )
    } catch (error) {
      item.status = 'error'
      item.error = error.message
      if (isJobGone(error)) store.queue.length = 0
      if (retryable()) this.report(file, error)
    } finally {
      store.active--
      this.pump(store)
    }
  },

  async presign(file) {
    return presignFile(presignPath, file)
  },

  put(url, file, type, item, store) {
    return putFile(url, file, type, item, store)
  },

  async attach(file, id, item, store) {
    const html = await attachFile(path, file, id)

    item.progress = 100
    // Drop the row before the swap so it converts straight into the
    // server-rendered file row without a flicker of both.
    removeRow(store, item)
    renderStreamMessage(html)
  },

  async report(file, error) {
    await reportFailure(errorPath, jobId, file, error)
  },
})

// Shared per-job upload state lives outside the component: streams only touch
// individual file rows, but the store must still survive any row swap so
// progress bars of in-flight uploads keep working.
const stores = new Map()

const storeFor = (path, jobId) => {
  if (!stores.has(path))
    stores.set(
      path,
      window.Alpine.reactive({
        jobId,
        queue: [], // pending { file, item } pairs
        items: [], // rendered per-file rows
        xhrs: new Set(),
        active: 0,
      }),
    )
  return stores.get(path)
}

const CONCURRENCY = 3
const RETRY_ATTEMPTS = 2
const RETRY_DELAY = 500

const failure = (stage, status, message, extras = {}) =>
  Object.assign(new Error(message), { stage, status, ...extras })

const isNetworkFailure = (error) => error.status == null
const isGatewayFailure = (error) =>
  error.status === 502 || error.status === 503 || error.status === 504

// A 404/410 on attach means the job is gone (deleted or purchase
// completed elsewhere). No queued file can still succeed.
const isJobGone = (error) =>
  error.stage === 'attach' &&
  (error.status === 404 || error.status === 410)

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

// HTTP errors are deterministic, except for gateway statuses on attach:
// the endpoint is idempotent, and 502/503/504 usually mean the request
// landed but the response was lost.
const retry = async (
  fn,
  shouldRetry,
  attempts = RETRY_ATTEMPTS,
  canRetryError = isNetworkFailure,
) => {
  try {
    return await fn()
  } catch (error) {
    if (!canRetryError(error) || attempts <= 0 || !shouldRetry()) throw error
    await sleep(RETRY_DELAY)
    return retry(fn, shouldRetry, attempts - 1, canRetryError)
  }
}

const presignUrl = (presignPath, file) => {
  const params = new URLSearchParams({ filename: file.name })
  if (file.type) params.set('type', file.type)
  return `${presignPath}?${params}`
}

const presignResponse = async (url) => {
  let response
  try {
    response = await fetch(url, { headers: { Accept: 'application/json' } })
  } catch (error) {
    throw failure('presign', null, `Upload could not start (${error.message})`)
  }

  if (!response.ok)
    throw failure(
      'presign',
      response.status,
      `Upload could not start (${response.status})`,
    )

  return response
}

const presignBody = async (response) => {
  try {
    return await response.text()
  } catch (error) {
    throw failure(
      'presign',
      response.status,
      `Upload could not start (${error.message})`,
    )
  }
}

const parsePresign = (status, body) => {
  try {
    return JSON.parse(body)
  } catch {
    throw failure(
      'presign',
      status,
      'Upload could not start (invalid presign response)',
      { response: body.slice(0, 1000) },
    )
  }
}

const presignFile = async (presignPath, file) => {
  const response = await presignResponse(presignUrl(presignPath, file))
  return parsePresign(response.status, await presignBody(response))
}

const putFile = (url, file, type, item, store) =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    store.xhrs.add(xhr)
    item.progress = 0

    xhr.open('PUT', url)
    xhr.setRequestHeader('Content-Type', type)
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable)
        item.progress = (event.loaded / event.total) * 100
    }
    xhr.onload = () => {
      store.xhrs.delete(xhr)
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve()
      } else {
        reject(
          failure('put', xhr.status, `Upload failed (${xhr.status})`, {
            content_type: type,
            origin: originOf(url),
            response: xhr.responseText,
          }),
        )
      }
    }
    xhr.onerror = () => {
      store.xhrs.delete(xhr)
      reject(
        failure('put', null, 'Upload failed', {
          content_type: type,
          origin: originOf(url),
        }),
      )
    }
    xhr.send(file)
  })

const attachPayload = (file, id) => ({
  file: {
    id,
    storage: 'cache',
    metadata: {
      filename: file.name,
      size: file.size,
      mime_type: file.type || 'application/octet-stream',
    },
  },
})

const attachResponse = async (path, file, id) => {
  let response
  try {
    response = await fetch(path, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/vnd.turbo-stream.html, application/json',
        'X-CSRF-Token': getCSRFToken(),
      },
      body: JSON.stringify(attachPayload(file, id)),
    })
  } catch (error) {
    throw failure('attach', null, `Upload failed (${error.message})`)
  }

  if (!response.ok) {
    const body = await response.json().catch(() => null)
    throw failure(
      'attach',
      response.status,
      (body && body.error) || `Upload failed (${response.status})`,
      { response: body ? JSON.stringify(body) : null },
    )
  }

  return response
}

const attachStream = async (response) => {
  try {
    return await response.text()
  } catch (error) {
    throw failure(
      'attach',
      response.status,
      `Upload failed (${error.message})`,
    )
  }
}

const attachFile = async (path, file, id) =>
  attachStream(await attachResponse(path, file, id))

const reportPayload = (jobId, file, error) => ({
  stage: error.stage || null,
  status: error.status ?? null,
  message: error.message,
  job_id: jobId,
  user_agent: globalThis.navigator?.userAgent || null,
  content_type: error.content_type || file.type || null,
  url_origin: error.origin || null,
  response: error.response ? String(error.response).slice(0, 1000) : null,
  file: {
    name: file.name,
    size: file.size,
    type: file.type || null,
  },
})

const reportFailure = async (errorPath, jobId, file, error) => {
  try {
    await fetch(errorPath, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': getCSRFToken(),
      },
      keepalive: true,
      body: JSON.stringify(reportPayload(jobId, file, error)),
    })
  } catch {
    // Reporting is best effort, the user already saw the failure.
  }
}

const originOf = (url) => {
  try {
    return new URL(url).origin
  } catch {
    return null
  }
}

const removeRow = (store, item) => {
  const index = store.items.findIndex((x) => x.key === item.key)
  if (index > -1) store.items.splice(index, 1)
}
